// ============================================
// Panel Generation Engine
// ============================================
//
// Creates synthetic lab panels at 5 complexity tiers
// with clinically plausible values. Uses seeded PRNG
// for reproducibility, Box-Muller for normal
// distributions, correlation engine for plausibility,
// and health profile biasing for scenario coverage.
// ============================================

import type {
  PanelGeneratorConfig,
  GeneratedPanel,
  LabValue,
  Demographics,
  CatalogBiomarker,
  LongitudinalConfig,
  LongitudinalPanel,
} from '../../types/patientJourney';
import { BIOMARKER_CATALOG, getBiomarkersForTier } from '../../data/biomarkers/biomarkerCatalog';
import { applyCorrelations } from './correlationEngine';

// --- ID Generation ---

function generateId(prefix: string): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  const hex = () => Math.floor(Math.random() * 0x10000).toString(16).padStart(4, '0');
  return `${prefix}_${hex()}${hex()}-${hex()}-${hex()}-${hex()}-${hex()}${hex()}${hex()}`;
}

// --- Seeded PRNG (Mulberry32) ---

function seededRandom(seed: number): () => number {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// --- Box-Muller Normal Distribution ---

function boxMullerNormal(rng: () => number): number {
  let u1 = rng();
  let u2 = rng();
  while (u1 === 0) u1 = rng();
  return Math.sqrt(-2.0 * Math.log(u1)) * Math.cos(2.0 * Math.PI * u2);
}

function sampleNormal(
  mean: number,
  stdDev: number,
  rng: () => number,
  skew?: number,
  floor?: number,
  ceiling?: number,
): number {
  let z = boxMullerNormal(rng);

  if (skew && skew !== 0) {
    const delta = skew / Math.sqrt(1 + skew * skew);
    const u0 = z;
    const v = boxMullerNormal(rng);
    z = delta * Math.abs(u0) + Math.sqrt(1 - delta * delta) * v;
  }

  let value = mean + stdDev * z;

  if (floor !== undefined) value = Math.max(floor, value);
  if (ceiling !== undefined) value = Math.min(ceiling, value);

  return Math.round(value * 100) / 100;
}

// --- Tier Biomarker Selection ---

export function selectBiomarkersForTier(
  tier: 1 | 2 | 3 | 4 | 5,
  verticals: string[],
): CatalogBiomarker[] {
  const effectiveTier = tier === 5 ? 4 : tier;
  let biomarkers = getBiomarkersForTier(effectiveTier as 1 | 2 | 3 | 4);

  const verticalCategories = new Set<string>();
  for (const v of verticals) {
    switch (v) {
      case 'hormones_women':
      case 'fertility':
        verticalCategories.add('hormones_women');
        verticalCategories.add('fertility');
        break;
      case 'hormones_men':
        verticalCategories.add('hormones_men');
        break;
      case 'metabolic':
      case 'weight_management':
        verticalCategories.add('metabolic');
        verticalCategories.add('cmp');
        break;
      case 'thyroid':
        verticalCategories.add('thyroid');
        break;
      case 'cardiovascular':
        verticalCategories.add('lipid_standard');
        verticalCategories.add('lipid_advanced');
        verticalCategories.add('inflammation');
        break;
      case 'longevity':
        verticalCategories.add('growth_aging');
        verticalCategories.add('inflammation');
        verticalCategories.add('nutrients');
        break;
      default:
        break;
    }
  }

  if (verticalCategories.size > 0 && tier <= 2) {
    biomarkers = biomarkers.filter(
      (b) => b.categories.some((c) => verticalCategories.has(c)) || b.tierMin === 1,
    );
  }

  return biomarkers;
}

// --- Value Generation ---

export function generateFromDistribution(biomarker: CatalogBiomarker, seed?: number): number {
  const rng = seededRandom(seed ?? Math.floor(Math.random() * 2147483647));
  return sampleNormal(
    biomarker.distribution.mean,
    biomarker.distribution.stdDev,
    rng,
    biomarker.distribution.skew,
    biomarker.distribution.floor,
    biomarker.distribution.ceiling,
  );
}

// --- Value Classification ---

export function classifyValue(
  value: number,
  biomarker: CatalogBiomarker,
): LabValue['status'] {
  const { conventionalRange: conv, functionalRange: func } = biomarker;

  if (value < conv.low) return 'below_conventional';
  if (value > conv.high) return 'above_conventional';
  if (value < func.low) return 'below_functional';
  if (value > func.high) return 'above_functional';
  return 'optimal';
}

// --- Health Profile Biasing ---

export function applyHealthProfileBias(
  values: Map<string, number>,
  profile: PanelGeneratorConfig['healthProfile'],
  biomarkers: CatalogBiomarker[],
): void {
  if (!profile) return;

  const profileTargets: Record<string, { optimalFrac: number; shiftStrength: number }> = {
    optimal: { optimalFrac: 0.85, shiftStrength: 0.7 },
    mixed: { optimalFrac: 0.50, shiftStrength: 0.3 },
    concerning: { optimalFrac: 0.30, shiftStrength: 0.5 },
    critical: { optimalFrac: 0.15, shiftStrength: 0.8 },
  };

  const target = profileTargets[profile];
  if (!target) return;

  for (const biomarker of biomarkers) {
    const current = values.get(biomarker.name);
    if (current === undefined) continue;

    const funcMid =
      (biomarker.functionalRange.low + biomarker.functionalRange.high) / 2;

    if (profile === 'optimal') {
      const shift = (funcMid - current) * target.shiftStrength;
      values.set(biomarker.name, Math.round((current + shift) * 100) / 100);
    } else if (profile === 'critical') {
      const convLow = biomarker.conventionalRange.low;
      const convHigh = biomarker.conventionalRange.high;
      const pushToward = current < funcMid ? convLow * 0.85 : convHigh * 1.15;
      const shift = (pushToward - current) * target.shiftStrength;
      values.set(biomarker.name, Math.round((current + shift) * 100) / 100);
    } else if (profile === 'concerning') {
      const funcLow = biomarker.functionalRange.low;
      const funcHigh = biomarker.functionalRange.high;
      const pushToward = current < funcMid ? funcLow * 0.9 : funcHigh * 1.1;
      const shift = (pushToward - current) * target.shiftStrength;
      values.set(biomarker.name, Math.round((current + shift) * 100) / 100);
    }
    // 'mixed' keeps values largely as generated
  }
}

// --- Trigger Application ---

function applyTriggerTargets(
  values: Map<string, number>,
  triggers: PanelGeneratorConfig['triggerTargets'],
  biomarkers: CatalogBiomarker[],
  rng: () => number,
): string[] {
  const triggerNames: string[] = [];
  if (!triggers) return triggerNames;

  for (const trigger of triggers) {
    const biomarker = biomarkers.find((b) => b.id === trigger.biomarkerId || b.name === trigger.biomarkerId);
    if (!biomarker) continue;

    let targetValue: number;
    const spread = rng() * 0.3 + 0.1;

    switch (trigger.targetStatus) {
      case 'below_conventional': {
        const range = biomarker.conventionalRange.low - biomarker.distribution.floor;
        targetValue = biomarker.conventionalRange.low - range * spread;
        break;
      }
      case 'above_conventional': {
        const range = biomarker.distribution.ceiling - biomarker.conventionalRange.high;
        targetValue = biomarker.conventionalRange.high + range * spread;
        break;
      }
      case 'below_functional': {
        const range = biomarker.functionalRange.low - biomarker.conventionalRange.low;
        targetValue = biomarker.conventionalRange.low + range * (0.2 + rng() * 0.5);
        break;
      }
      case 'above_functional': {
        const range = biomarker.conventionalRange.high - biomarker.functionalRange.high;
        targetValue = biomarker.functionalRange.high + range * (0.2 + rng() * 0.5);
        break;
      }
      default:
        continue;
    }

    targetValue = Math.max(biomarker.distribution.floor, Math.min(biomarker.distribution.ceiling, targetValue));
    values.set(biomarker.name, Math.round(targetValue * 100) / 100);
    triggerNames.push(biomarker.name);
  }

  return triggerNames;
}

// --- Gender Filtering ---

function filterByGender(biomarkers: CatalogBiomarker[], sex: Demographics['sex']): CatalogBiomarker[] {
  return biomarkers.filter((b) => {
    if (b.gender === 'any') return true;
    return b.gender === sex;
  });
}

// --- Narrative Generation ---

function buildClinicalNarrative(
  labs: LabValue[],
  correlationsApplied: string[],
  demographics: Demographics,
): string {
  const triggers = labs.filter((l) => l.trigger);
  const abnormals = labs.filter(
    (l) => l.status !== 'optimal' && !l.trigger,
  );

  const parts: string[] = [];
  parts.push(
    `${demographics.age}-year-old ${demographics.sex.toLowerCase()} from ${demographics.state}.`,
  );

  if (triggers.length > 0) {
    parts.push(
      `Trigger biomarkers: ${triggers.map((t) => `${t.biomarker} (${t.value} ${t.unit}, ${t.status})`).join('; ')}.`,
    );
  }

  if (abnormals.length > 0) {
    parts.push(`${abnormals.length} additional biomarker(s) outside functional range.`);
  }

  if (correlationsApplied.length > 0) {
    parts.push(`${correlationsApplied.length} correlation rule(s) applied for clinical plausibility.`);
  }

  const optimalCount = labs.filter((l) => l.status === 'optimal').length;
  const pct = Math.round((optimalCount / labs.length) * 100);
  parts.push(`Overall: ${pct}% of biomarkers in functional optimal range.`);

  return parts.join(' ');
}

// --- Main Panel Generator ---

export function generatePanel(config: PanelGeneratorConfig): GeneratedPanel {
  const seed = config.seed ?? Math.floor(Math.random() * 2147483647);
  const rng = seededRandom(seed);

  const effectiveTier = config.tier === 5 ? 4 : config.tier;
  let selectedBiomarkers = selectBiomarkersForTier(effectiveTier as 1 | 2 | 3 | 4 | 5, config.verticals);
  selectedBiomarkers = filterByGender(selectedBiomarkers, config.demographics.sex);

  // Generate raw values
  const rawValues = new Map<string, number>();
  for (const biomarker of selectedBiomarkers) {
    const value = sampleNormal(
      biomarker.distribution.mean,
      biomarker.distribution.stdDev,
      rng,
      biomarker.distribution.skew,
      biomarker.distribution.floor,
      biomarker.distribution.ceiling,
    );
    rawValues.set(biomarker.name, Math.round(value * 100) / 100);
  }

  // Apply trigger targets
  const triggerNames = applyTriggerTargets(rawValues, config.triggerTargets, selectedBiomarkers, rng);

  // Apply health profile bias
  applyHealthProfileBias(rawValues, config.healthProfile, selectedBiomarkers);

  // Apply correlation engine
  const { adjustedValues, correlationsApplied } = applyCorrelations(rawValues);

  // Build LabValue array
  const labs: LabValue[] = [];
  for (const biomarker of selectedBiomarkers) {
    const value = adjustedValues.get(biomarker.name);
    if (value === undefined) continue;

    const clamped = Math.max(
      biomarker.distribution.floor,
      Math.min(biomarker.distribution.ceiling, value),
    );
    const rounded = Math.round(clamped * 100) / 100;
    const status = classifyValue(rounded, biomarker);

    labs.push({
      biomarker: biomarker.name,
      value: rounded,
      unit: biomarker.unit,
      conventionalRange: `${biomarker.conventionalRange.low}-${biomarker.conventionalRange.high}`,
      functionalRange: `${biomarker.functionalRange.low}-${biomarker.functionalRange.high}`,
      xPrizeRange: `${biomarker.xPrizeRange.low}-${biomarker.xPrizeRange.high}`,
      status,
      trigger: triggerNames.includes(biomarker.name),
      timestamp: new Date().toISOString(),
    });
  }

  const multiVerticalTriggers = triggerNames.filter((name) => {
    const bm = selectedBiomarkers.find((b) => b.name === name);
    return bm && bm.categories.length > 1;
  });

  const narrative = buildClinicalNarrative(labs, correlationsApplied, config.demographics);

  return {
    panelId: generateId('panel'),
    tier: config.tier,
    generatedAt: new Date().toISOString(),
    seed,
    demographics: config.demographics,
    labs,
    correlationsApplied,
    clinicalNarrative: narrative,
    triggerBiomarkers: triggerNames,
    expectedProtocols: inferProtocols(labs, config.verticals),
    expectedProducts: inferProducts(labs),
    multiVerticalTriggers,
  };
}

// --- Protocol / Product Inference ---

function inferProtocols(labs: LabValue[], verticals: string[]): string[] {
  const protocols: string[] = [];
  const triggers = labs.filter((l) => l.trigger || l.status === 'above_conventional' || l.status === 'below_conventional');

  for (const lab of triggers) {
    switch (lab.biomarker) {
      case 'TSH':
      case 'Free T4':
      case 'Free T3':
        if (!protocols.includes('thyroid_support')) protocols.push('thyroid_support');
        break;
      case 'Fasting Insulin':
      case 'Fasting Glucose':
      case 'Hemoglobin A1c':
      case 'HOMA-IR':
        if (!protocols.includes('metabolic_optimization')) protocols.push('metabolic_optimization');
        break;
      case 'Testosterone Total':
      case 'Free Testosterone':
      case 'SHBG':
        if (!protocols.includes('hormone_optimization_male')) protocols.push('hormone_optimization_male');
        break;
      case 'Estradiol':
      case 'Progesterone':
      case 'FSH':
        if (!protocols.includes('hormone_optimization_female')) protocols.push('hormone_optimization_female');
        break;
      case 'Vitamin D (25-OH)':
      case 'B12':
      case 'Folate':
      case 'Iron':
      case 'Ferritin':
        if (!protocols.includes('nutrient_repletion')) protocols.push('nutrient_repletion');
        break;
      case 'hs-CRP':
      case 'Homocysteine':
      case 'ESR':
        if (!protocols.includes('inflammation_management')) protocols.push('inflammation_management');
        break;
      case 'Total Cholesterol':
      case 'LDL':
      case 'HDL':
      case 'Triglycerides':
        if (!protocols.includes('lipid_optimization')) protocols.push('lipid_optimization');
        break;
    }
  }

  for (const v of verticals) {
    if (v === 'longevity' && !protocols.includes('longevity_protocol')) {
      protocols.push('longevity_protocol');
    }
  }

  return protocols;
}

function inferProducts(labs: LabValue[]): string[] {
  const products: string[] = [];
  for (const lab of labs) {
    if (lab.status === 'optimal') continue;
    switch (lab.biomarker) {
      case 'Vitamin D (25-OH)':
        products.push('Vitamin D3+K2');
        break;
      case 'B12':
        products.push('Methylcobalamin B12');
        break;
      case 'Folate':
        products.push('Methylfolate');
        break;
      case 'Iron':
      case 'Ferritin':
        products.push('Iron Bisglycinate');
        break;
      case 'Magnesium RBC':
        products.push('Magnesium Glycinate');
        break;
      case 'hs-CRP':
        products.push('Omega-3 EPA/DHA');
        break;
      case 'Fasting Insulin':
      case 'HOMA-IR':
        products.push('Berberine Complex');
        break;
      case 'Homocysteine':
        products.push('B-Complex (Methylated)');
        break;
    }
  }
  return [...new Set(products)];
}

// --- Longitudinal Panel Generator ---

export function generateLongitudinalPanel(config: LongitudinalConfig): LongitudinalPanel {
  const draws: GeneratedPanel[] = [];
  const baseSeed = config.seed ?? Math.floor(Math.random() * 2147483647);

  for (let i = 0; i < config.drawCount; i++) {
    const drawSeed = baseSeed + i * 7919;

    let healthProfile = config.healthProfile;
    if (config.progressionPattern === 'improvement') {
      const profiles: PanelGeneratorConfig['healthProfile'][] = ['concerning', 'mixed', 'mixed', 'optimal'];
      healthProfile = profiles[Math.min(i, profiles.length - 1)];
    } else if (config.progressionPattern === 'worsening') {
      const profiles: PanelGeneratorConfig['healthProfile'][] = ['mixed', 'concerning', 'concerning', 'critical'];
      healthProfile = profiles[Math.min(i, profiles.length - 1)];
    } else if (config.progressionPattern === 'plateau') {
      healthProfile = config.healthProfile ?? 'mixed';
    }
    // 'mixed' and 'new_variable' keep the configured profile or randomize

    const drawConfig: PanelGeneratorConfig = {
      ...config,
      tier: 4,
      seed: drawSeed,
      healthProfile,
    };

    const panel = generatePanel(drawConfig);
    // Adjust timestamp for draw interval
    const drawDate = new Date();
    drawDate.setDate(drawDate.getDate() + i * config.drawIntervalDays);
    for (const lab of panel.labs) {
      lab.timestamp = drawDate.toISOString();
    }

    draws.push(panel);
  }

  const interventions = (config.interventionBetweenDraws ?? []).map(
    (intv) => `After draw ${intv.afterDraw}: ${intv.type} — ${intv.details}`,
  );

  const expectedJourneyPath = buildJourneyPath(config.progressionPattern, config.drawCount);

  const expectedGateTransitions = inferGateTransitions(
    draws,
    config.progressionPattern,
  );

  return {
    panelSetId: generateId('panelset'),
    draws,
    progressionPattern: config.progressionPattern,
    interventions,
    expectedJourneyPath,
    expectedGateTransitions,
  };
}

function buildJourneyPath(pattern: string, drawCount: number): string[] {
  const path: string[] = ['first_contact', 'checkout'];
  for (let i = 1; i < drawCount; i++) {
    if (i === 1) path.push('mid_protocol');
    if (i >= 1) path.push('retest');
  }
  if (pattern === 'improvement') {
    path.push('protocol_success');
  } else if (pattern === 'worsening') {
    path.push('gate2_transition');
  }
  return path;
}

function inferGateTransitions(
  draws: GeneratedPanel[],
  pattern: string,
): LongitudinalPanel['expectedGateTransitions'] {
  const transitions: LongitudinalPanel['expectedGateTransitions'] = [];

  if (pattern === 'worsening' && draws.length >= 2) {
    transitions.push({
      fromGate: 1,
      toGate: 2,
      afterDraw: Math.min(2, draws.length),
      reason: 'Worsening labs despite protocol — escalation to telehealth/Rx',
    });
  }

  if (pattern === 'new_variable' && draws.length >= 3) {
    transitions.push({
      fromGate: 1,
      toGate: 2,
      afterDraw: 3,
      reason: 'New abnormal biomarker introduced — requires clinical evaluation',
    });
  }

  return transitions;
}

export { BIOMARKER_CATALOG, getBiomarkersForTier };
